import React, { useState, useCallback } from "react";
import { ButtonH } from "components/UiKit/ButtonH";
import { Ico } from "components/Uikit";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { message, Modal, Select, Checkbox, Tag } from "antd";
import { FAST_API_URL, postOptions } from "api/index";
import { MetadataToSubtitlesLanguages } from "providers/AudioProvider/types";
import { useIsEnglishUi } from "utils/uiLanguage";
import { AutoEditGate, useAutoEditBlock } from "../../../ManualOnly/AutoEditGate";

const spinKf = `@keyframes btkSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`;

const LANGUAGES = [
    { value: "english", label: "English" },
    { value: "spanish", label: "Spanish" },
    { value: "german", label: "German" },
    { value: "chinese", label: "Chinese" },
    { value: "arabic", label: "Arabic" },
    { value: "russian", label: "Russian" },
];

const SOURCE_LANGUAGES = [
    { value: "russian", label: "Russian" },
    { value: "english", label: "English" },
];

interface BulkResult {
    total: number;
    translated: number;
    skipped: number;
    failed: number;
    details: { id: string; status: string; error?: string }[];
}

export const TranslateFolderBulk: React.FC = () => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [langFrom, setLangFrom] = useState("russian");
    const [langTo, setLangTo] = useState("english");
    const [reTranslate, setReTranslate] = useState(false);
    const [result, setResult] = useState<BulkResult | null>(null);

    const fromEng = useIsEnglishUi();
    // Машинный перевод всей папки (а с «перевести заново» — поверх сделанного) — автоправка.
    const autoEdit = useAutoEditBlock();

    const handleOpen = useCallback(() => {
        if (autoEdit.blocked) return;
        setResult(null);
        // Pre-fill langTo from current state
        if (state.lang) {
            const currentLang = Object.entries(MetadataToSubtitlesLanguages).find(
                ([_, v]) => String(v) === String(state.lang)
            );
            if (currentLang) {
                setLangTo(currentLang[0]);
            }
        }
        setShowModal(true);
    }, [state.lang, autoEdit.blocked]);

    const handleTranslate = useCallback(async () => {
        if (autoEdit.blocked) return;
        if (!state.mediaId || !state.folder) {
            message.error("No media/folder selected");
            return;
        }

        setLoading(true);
        setResult(null);

        try {
            const response = await fetch(
                `${FAST_API_URL}/translate_folder_bulk?media=${state.mediaId}`,
                {
                    ...postOptions,
                    body: JSON.stringify({
                        folder_num: parseInt(state.folder.toString(), 10),
                        language_from: langFrom,
                        language_to: langTo,
                        re_translate: reTranslate,
                    }),
                }
            );

            if (!response.ok) {
                const err = await response.json().catch(() => ({ detail: "Request failed" }));
                throw new Error(err.detail || "Request failed");
            }

            const data: BulkResult = await response.json();
            setResult(data);

            if (data.translated > 0) {
                message.success(
                    fromEng
                        ? `Translated ${data.translated} subtitles`
                        : `Переведено ${data.translated} субтитров`
                );
                handles.refreshAllSubtitles();
            } else if (data.skipped > 0) {
                message.info(
                    fromEng
                        ? `All ${data.skipped} subtitles already translated`
                        : `Все ${data.skipped} субтитров уже переведены`
                );
            }
        } catch (e: any) {
            message.error(e.message || "Translation failed");
        } finally {
            setLoading(false);
        }
    }, [state.mediaId, state.folder, langFrom, langTo, reTranslate, fromEng, handles, autoEdit.blocked]);

    return (
        <>
            <style>{spinKf}</style>
            <AutoEditGate {...autoEdit}>
                <ButtonH sm ghost onClick={handleOpen} disabled={loading || autoEdit.blocked}>
                    <Ico
                        size={"16"}
                        style={
                            loading
                                ? { animation: "btkSpin 1s linear infinite", display: "inline-block" }
                                : undefined
                        }
                    >
                        translate
                    </Ico>
                    {fromEng ? "Translate" : "Перевести"}
                </ButtonH>
            </AutoEditGate>

            <Modal
                title={fromEng ? "Translate Folder" : "Перевод папки"}
                open={showModal}
                onCancel={() => setShowModal(false)}
                footer={null}
                width={500}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {/* Language From */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 100, fontWeight: 500 }}>
                            {fromEng ? "From:" : "Из:"}
                        </span>
                        <Select
                            value={langFrom}
                            onChange={setLangFrom}
                            options={SOURCE_LANGUAGES}
                            style={{ flex: 1 }}
                        />
                    </div>

                    {/* Language To */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 100, fontWeight: 500 }}>
                            {fromEng ? "To:" : "В:"}
                        </span>
                        <Select
                            value={langTo}
                            onChange={setLangTo}
                            options={LANGUAGES}
                            style={{ flex: 1 }}
                        />
                    </div>

                    {/* Re-translate checkbox */}
                    <Checkbox
                        checked={reTranslate}
                        onChange={(e) => setReTranslate(e.target.checked)}
                    >
                        {fromEng
                            ? "Re-translate existing (overwrite)"
                            : "Перевести заново (перезаписать)"}
                    </Checkbox>

                    {/* Translate button */}
                    <ButtonH
                        onClick={handleTranslate}
                        disabled={loading || langFrom === langTo}
                        style={{ marginTop: 4 }}
                    >
                        <Ico
                            size={"16"}
                            style={
                                loading
                                    ? {
                                          animation: "btkSpin 1s linear infinite",
                                          display: "inline-block",
                                      }
                                    : undefined
                            }
                        >
                            {loading ? "hourglass_top" : "translate"}
                        </Ico>
                        {loading
                            ? fromEng
                                ? "Translating..."
                                : "Переводим..."
                            : fromEng
                            ? "Translate Folder"
                            : "Перевести папку"}
                    </ButtonH>

                    {/* Results */}
                    {result && (
                        <div
                            style={{
                                marginTop: 8,
                                padding: 12,
                                background: "#f6f6f6",
                                borderRadius: 6,
                                border: "1px solid #e8e8e8",
                            }}
                        >
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                {fromEng ? "Results:" : "Результат:"}
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <Tag color="blue">
                                    {fromEng ? "Total" : "Всего"}: {result.total}
                                </Tag>
                                {result.translated > 0 && (
                                    <Tag color="green">
                                        {fromEng ? "Translated" : "Переведено"}: {result.translated}
                                    </Tag>
                                )}
                                {result.skipped > 0 && (
                                    <Tag color="orange">
                                        {fromEng ? "Skipped" : "Пропущено"}: {result.skipped}
                                    </Tag>
                                )}
                                {result.failed > 0 && (
                                    <Tag color="red">
                                        {fromEng ? "Failed" : "Ошибки"}: {result.failed}
                                    </Tag>
                                )}
                            </div>
                            {result.failed > 0 && (
                                <div style={{ marginTop: 8, fontSize: 12, color: "#ff4d4f" }}>
                                    {result.details
                                        .filter((d) => d.status === "error")
                                        .map((d, i) => (
                                            <div key={i}>
                                                {d.id}: {d.error}
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};
